import React, { useRef, useCallback, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import './RichEditor.css';
import { articleTemplates, articleSnippets } from '../config/articleTemplates';
import MediaEmbedModal from './MediaEmbedModal';
import axiosInstance from '../services/axiosInstance';
import { usePermissions } from '../hooks/usePermissions';
import { determineAiScope, AiScope, AI_REFINE_MAX_CONTENT, AI_REFINE_COLLEGATO_MAX_CONTENT } from '../utils/aiRefine';
import { pulisciStiliCarattere, testoInHtml } from '../utils/pulisciStiliCarattere';

interface RichEditorProps {
  value: string;
  onChange: (content: string) => void;
  height?: number;
  placeholder?: string;
  disabled?: boolean;
  mergeTags?: { category: string; tags: { tag: string; label: string }[] }[];
  /** Toolbar essenziale su una riga scrollabile (nasconde Template/Elementi/Allineamento/Guida).
      Pensata per contesti compatti come la Diretta; l'editor articoli resta full-featured. */
  compact?: boolean;
  /** Abilita il pulsante "AI" nella toolbar (modifica della bozza via OpenAI).
      Attivare solo nell'editor articoli; richiede anche il permesso articles.ai_refine. */
  enableAi?: boolean;
  /** Nasconde il pulsante "Media" (audio/video/YouTube). Usato dalle circolari, dove i
      contenuti incorporati verrebbero tolti dal filtro perché nelle email non funzionano. */
  hideMedia?: boolean;
  /** Toglie carattere, dimensione e interlinea dall'HTML incollato e da quello caricato:
      sono quelli del sito e della mail. Solo per comunicazioni e circolari. */
  pulisciStiliCarattere?: boolean;
  /** Funzione AI propria (es. verbali): se presente il pulsante "AI" compare senza il
      permesso articles.ai_refine e la modifica usa questa funzione al posto di
      POST /admin/ai/refine-content. Restituisce l'HTML da applicare. */
  aiRefine?: (args: { content: string; instruction: string; scope: AiScope }) => Promise<string>;
  /** Azioni rapide mostrate in una bolla sopra il testo selezionato nell'editor. */
  aiAzioniRapide?: { etichetta: string; istruzione: string }[];
  /** Classi aggiunte all'area modificabile (es. foglio serif del verbale). */
  editorClassName?: string;
  /** Avviso mostrato nel pannello «AI» (es. verbale già pubblicato). */
  aiAvviso?: string;
}

const RichEditor: React.FC<RichEditorProps> = ({
  value,
  onChange,
  height = 500,
  placeholder = 'Inizia a scrivere il tuo articolo...',
  disabled = false,
  mergeTags,
  compact = false,
  enableAi = false,
  hideMedia = false,
  pulisciStiliCarattere: pulisciCarattere = false,
  aiRefine,
  aiAzioniRapide,
  editorClassName,
  aiAvviso
}) => {
  const toast = useToast();
  const { hasPermission } = usePermissions();
  const editorRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHtmlMode, setIsHtmlMode] = React.useState(false);
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);
  const [showMediaModal, setShowMediaModal] = React.useState(false);
  const ultimoPulitoRef = useRef<string | null>(null);

  // Aggiorna il contenuto dell'editor quando value cambia o quando si cambia modalità
  // Con pulisciStiliCarattere il valore caricato si mostra ripulito e si passa al
  // genitore, così una bozza vecchia esce pulita al primo salvataggio
  useEffect(() => {
    const mostrato = pulisciCarattere ? pulisciStiliCarattere(value) : value;
    if (!isHtmlMode && editorRef.current && editorRef.current.innerHTML !== mostrato) {
      editorRef.current.innerHTML = mostrato;
      // Testo cambiato dall'esterno (generazione, ripristino, «Applica»): «Annulla modifica AI»
      // rimetterebbe un testo superato
      if (aiSnapshotRef.current !== null) {
        aiSnapshotRef.current = null;
        setHasAiSnapshot(false);
      }
    }
    // una volta per valore: niente giri a vuoto se la pulizia non fosse stabile
    if (pulisciCarattere && !disabled && mostrato !== value && ultimoPulitoRef.current !== value) {
      ultimoPulitoRef.current = mostrato;
      onChange(mostrato);
    }
  }, [value, isHtmlMode, pulisciCarattere, disabled, onChange]);

  // Gestisce il focus quando si cambia modalità
  useEffect(() => {
    if (isHtmlMode && textareaRef.current) {
      textareaRef.current.focus();
    } else if (!isHtmlMode && editorRef.current) {
      editorRef.current.focus();
    }
  }, [isHtmlMode]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  const insertTemplate = useCallback((template: string) => {
    if (isHtmlMode) {
      onChange(template);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } else {
      if (editorRef.current) {
        editorRef.current.innerHTML = template;
        onChange(template);
        editorRef.current.focus();
      }
    }
  }, [onChange, isHtmlMode]);

  const handleHtmlChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  const toggleHtmlMode = useCallback(() => {
    setIsHtmlMode(prev => {
      const newMode = !prev;

      // Quando passiamo dalla modalità HTML alla modalità visuale,
      // forziamo l'aggiornamento del contenuto
      if (!newMode) {
        // Usiamo requestAnimationFrame per assicurarci che il DOM sia pronto
        requestAnimationFrame(() => {
          if (editorRef.current && value) {
            editorRef.current.innerHTML = value;
            // Forza un re-render se necessario
            editorRef.current.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      }

      return newMode;
    });
  }, [value]);

  // Gestisce il caricamento dell'immagine
  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validazione tipo file
    if (!file.type.startsWith('image/')) {
      toast.error('Formato non valido. Seleziona un\'immagine.');
      return;
    }

    // Validazione dimensione (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'immagine è troppo grande. Massimo 5MB.');
      return;
    }

    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Usa REACT_APP_API_BASE_URL che include già /api
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Errore durante il caricamento dell\'immagine');
      }

      const data = await response.json();
      // Il backend restituisce { success: true, file: { url: '...', ... } }
      const imageUrl = data.file?.url || data.url;

      // Inserisci l'immagine nell'editor
      if (isHtmlMode) {
        // In modalità HTML, inserisci il tag img
        const imgTag = `<img src="${imageUrl}" alt="${file.name}" style="max-width: 100%; height: auto;" />`;
        onChange(value + '\n' + imgTag);
      } else {
        // In modalità visuale, inserisci l'immagine usando execCommand
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = file.name;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';

        if (editorRef.current) {
          // Inserisci l'immagine alla posizione del cursore
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(img);

            // Sposta il cursore dopo l'immagine
            range.setStartAfter(img);
            range.setEndAfter(img);
            selection.removeAllRanges();
            selection.addRange(range);
          } else {
            // Se non c'è selezione, aggiungi alla fine
            editorRef.current.appendChild(img);
          }

          handleInput();
        }
      }

      toast.success('Immagine caricata con successo!');

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Errore caricamento immagine:', error);
      toast.error('Errore durante il caricamento dell\'immagine');
    } finally {
      setIsUploadingImage(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [value, onChange, isHtmlMode, handleInput]);

  const openImagePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Inserisci link
  const insertLink = useCallback(() => {
    const url = prompt('Inserisci l\'URL del link:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  // Rimuovi formattazione
  const removeFormatting = useCallback(() => {
    execCommand('removeFormat');
  }, [execCommand]);

  // Inserisci linea orizzontale
  const insertHorizontalRule = useCallback(() => {
    execCommand('insertHorizontalRule');
  }, [execCommand]);

  // Stato per il menu dropdown dei template
  const [showTemplateMenu, setShowTemplateMenu] = React.useState(false);
  const [showSnippetMenu, setShowSnippetMenu] = React.useState(false);
  const [showMergeTagMenu, setShowMergeTagMenu] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);
  const templateMenuRef = useRef<HTMLDivElement>(null);
  const snippetMenuRef = useRef<HTMLDivElement>(null);
  const mergeTagMenuRef = useRef<HTMLDivElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const aiPanelRef = useRef<HTMLDivElement>(null);
  const aiSavedRangeRef = useRef<Range | null>(null);
  const aiSnapshotRef = useRef<string | null>(null);
  const aiApplyingRef = useRef(false);
  const aiLoadingRef = useRef(false);
  const [showAiPanel, setShowAiPanel] = React.useState(false);
  const [aiInstruction, setAiInstruction] = React.useState('');
  const [aiScope, setAiScope] = React.useState<AiScope>('full');
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiError, setAiError] = React.useState('');
  const [hasAiSnapshot, setHasAiSnapshot] = React.useState(false);
  // Con l'editor disabilitato (es. generazione del verbale in corso) l'AI non riscrive il testo
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  useEffect(() => {
    if (disabled) setShowAiPanel(false);
  }, [disabled]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const bollaRangeRef = useRef<Range | null>(null);
  const [bolla, setBolla] = React.useState<{ top: number; left: number; sotto: boolean } | null>(null);
  const [bollaInCorso, setBollaInCorso] = React.useState<number | null>(null);

  // Con aiRefine l'AI è quella del contesto (es. verbali): niente permesso articles.ai_refine
  const showAiButton = !isHtmlMode && (aiRefine ? true : enableAi && hasPermission('articles.ai_refine'));
  const aiMaxContent = aiRefine ? AI_REFINE_COLLEGATO_MAX_CONTENT : AI_REFINE_MAX_CONTENT;
  const azioniRapide = aiAzioniRapide && aiAzioniRapide.length > 0 ? aiAzioniRapide : null;
  const bollaAbilitata = showAiButton && !!azioniRapide && !disabled;

  // Apre il pannello AI: cattura la selezione corrente PRIMA che il focus si sposti
  const openAiPanel = useCallback(() => {
    setAiError('');
    let selectedText = '';
    const selection = window.getSelection();
    if (
      selection &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      editorRef.current &&
      editorRef.current.contains(selection.anchorNode)
    ) {
      aiSavedRangeRef.current = selection.getRangeAt(0).cloneRange();
      selectedText = selection.toString();
    } else {
      aiSavedRangeRef.current = null;
    }
    setAiScope(determineAiScope(selectedText));
    setShowAiPanel(true);
  }, []);

  // Estrae l'HTML di un Range
  const rangeHtml = useCallback((range: Range | null): string => {
    if (!range) return '';
    const div = document.createElement('div');
    div.appendChild(range.cloneContents());
    return div.innerHTML;
  }, []);

  /** Chiede la modifica all'AI e la applica (snapshot per «Annulla modifica AI»).
      Usata dal pannello e dalla bolla; restituisce il messaggio d'errore, null se applicata. */
  const eseguiModificaAi = useCallback(async (
    instruction: string,
    scope: AiScope,
    savedRange: Range | null,
    daBolla = false
  ): Promise<string | null> => {
    if (!editorRef.current || aiLoadingRef.current) return null;
    if (disabledRef.current) return 'Modifica non disponibile in questo momento.';

    // Per lo scope selezione, verifica che il Range salvato sia ancora valido (Minor 4)
    if (scope === 'selection') {
      if (!savedRange || !editorRef.current.contains(savedRange.commonAncestorContainer)) {
        return daBolla
          ? 'La selezione non è più valida: riseleziona il testo.'
          : 'La selezione non è più valida. Chiudi il pannello e riseleziona il testo.';
      }
    }

    const content = scope === 'selection' ? rangeHtml(savedRange) : editorRef.current.innerHTML;
    if (!content || content.trim().length === 0) {
      return 'Nessun contenuto da modificare.';
    }

    if (content.length > aiMaxContent) {
      if (scope === 'full') {
        return aiRefine
          ? `Il testo supera ${aiMaxContent} caratteri: seleziona una porzione di testo e applica la modifica solo a quella.`
          : `L'articolo supera ${aiMaxContent} caratteri: seleziona una porzione di testo e applica la modifica solo a quella.`;
      }
      return `La selezione supera ${aiMaxContent} caratteri: seleziona una porzione più piccola.`;
    }

    setAiLoading(true);
    aiLoadingRef.current = true;
    try {
      let html: string | undefined;
      if (aiRefine) {
        html = await aiRefine({ content, instruction, scope });
      } else {
        const response = await axiosInstance.post(
          '/admin/ai/refine-content',
          { content, instruction, scope },
          { timeout: 60000 }
        );
        html = response.data?.data?.html;
      }
      if (!html) {
        throw new Error('Risposta AI non valida');
      }
      const editor = editorRef.current;
      if (!editor) return "L'editor non è più disponibile: modifica AI non applicata.";
      if (disabledRef.current) return 'Modifica non disponibile in questo momento: modifica AI non applicata.';

      // Snapshot per l'annulla (prima di applicare)
      aiSnapshotRef.current = editor.innerHTML;

      // Flag per non invalidare lo snapshot durante la mutazione programmatica (Issue 1)
      aiApplyingRef.current = true;
      try {
        if (scope === 'selection' && savedRange) {
          // Inserimento nativo del browser: gestisce meglio la normalizzazione (Issue 3)
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(savedRange);
          editor.focus();
          const inserito = typeof document.execCommand === 'function'
            && document.execCommand('insertHTML', false, html);
          if (!inserito) {
            // Ripiego se insertHTML non è disponibile: sostituzione diretta del Range
            savedRange.deleteContents();
            // <template>: gli script dell'HTML restituito non vengono eseguiti
            const template = document.createElement('template');
            template.innerHTML = html;
            savedRange.insertNode(template.content);
          }
        } else {
          editor.innerHTML = html;
        }
        handleInput();
      } finally {
        aiApplyingRef.current = false;
      }

      setHasAiSnapshot(true);
      toast.success('Modifica AI applicata.');
      return null;
    } catch (error: any) {
      return error?.response?.data?.error || 'Errore durante la modifica AI. Riprova.';
    } finally {
      setAiLoading(false);
      aiLoadingRef.current = false;
    }
  }, [aiRefine, aiMaxContent, rangeHtml, handleInput, toast]);

  const handleAiApply = useCallback(async () => {
    if (!editorRef.current || aiLoadingRef.current || disabledRef.current) return;
    const instruction = aiInstruction.trim();
    if (instruction.length < 3) {
      setAiError('Scrivi un\'istruzione di almeno 3 caratteri.');
      return;
    }
    setAiError('');
    const errore = await eseguiModificaAi(instruction, aiScope, aiScope === 'selection' ? aiSavedRangeRef.current : null);
    if (errore) {
      setAiError(errore);
      return;
    }
    setShowAiPanel(false);
    setAiInstruction('');
  }, [aiInstruction, aiScope, eseguiModificaAi]);

  // Bolla sopra la selezione: posizione dal rettangolo della selezione (se il browser lo dà)
  const chiudiBolla = useCallback(() => {
    bollaRangeRef.current = null;
    setBolla(null);
  }, []);

  const aggiornaBolla = useCallback(() => {
    if (aiLoadingRef.current) return; // durante l'elaborazione la bolla resta com'è
    const selection = window.getSelection();
    const editor = editorRef.current;
    if (
      !selection ||
      selection.rangeCount === 0 ||
      selection.isCollapsed ||
      !editor ||
      !editor.contains(selection.anchorNode) ||
      !editor.contains(selection.focusNode) ||
      selection.toString().trim().length === 0
    ) {
      chiudiBolla();
      return;
    }
    const range = selection.getRangeAt(0);
    bollaRangeRef.current = range.cloneRange();
    let top = 0;
    let left = 0;
    let sotto = false;
    const wrapper = wrapperRef.current;
    if (wrapper && typeof range.getBoundingClientRect === 'function') {
      const r = range.getBoundingClientRect();
      const w = wrapper.getBoundingClientRect();
      left = r.left - w.left + r.width / 2;
      // La bolla è centrata sulla selezione: la si tiene dentro l'editor ai bordi
      if (w.width > 240) left = Math.min(Math.max(left, 120), w.width - 120);
      top = r.top - w.top - 8;
      // Troppo vicino al bordo alto: la bolla va sotto la selezione
      if (r.top < 56) {
        top = r.bottom - w.top + 8;
        sotto = true;
      }
    }
    setBolla({ top, left, sotto });
  }, [chiudiBolla]);

  useEffect(() => {
    if (!bollaAbilitata) {
      chiudiBolla();
      return;
    }
    const alCambioSelezione = () => aggiornaBolla();
    const alloScroll = () => { if (!aiLoadingRef.current) chiudiBolla(); };
    const alTasto = (e: KeyboardEvent) => { if (e.key === 'Escape' && !aiLoadingRef.current) chiudiBolla(); };
    document.addEventListener('selectionchange', alCambioSelezione);
    window.addEventListener('scroll', alloScroll, true);
    document.addEventListener('keydown', alTasto);
    return () => {
      document.removeEventListener('selectionchange', alCambioSelezione);
      window.removeEventListener('scroll', alloScroll, true);
      document.removeEventListener('keydown', alTasto);
    };
  }, [bollaAbilitata, aggiornaBolla, chiudiBolla]);

  const handleAzioneRapida = useCallback(async (indice: number) => {
    const azione = azioniRapide?.[indice];
    if (!azione || aiLoadingRef.current) return;
    setBollaInCorso(indice);
    const errore = await eseguiModificaAi(azione.istruzione, 'selection', bollaRangeRef.current, true);
    setBollaInCorso(null);
    chiudiBolla();
    if (errore) toast.error(errore);
  }, [azioniRapide, eseguiModificaAi, chiudiBolla, toast]);

  const handleAiUndo = useCallback(() => {
    if (aiSnapshotRef.current === null || !editorRef.current) return;
    editorRef.current.innerHTML = aiSnapshotRef.current;
    handleInput();
    aiSnapshotRef.current = null;
    setHasAiSnapshot(false);
    toast.success('Modifica AI annullata.');
  }, [handleInput, toast]);

  // Input MANUALE dell'utente nell'editor: invalida lo snapshot AI (Issue 1)
  const handleEditorInput = useCallback(() => {
    if (!aiApplyingRef.current && aiSnapshotRef.current !== null) {
      aiSnapshotRef.current = null;
      setHasAiSnapshot(false);
    }
    handleInput();
  }, [handleInput]);

  // Reset dello stato AI quando si cambia modalità HTML/Visuale (Issue 2)
  useEffect(() => {
    setShowAiPanel(false);
    setHasAiSnapshot(false);
    aiSnapshotRef.current = null;
    aiSavedRangeRef.current = null;
  }, [isHtmlMode]);

  // Chiudi i menu quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (templateMenuRef.current && !templateMenuRef.current.contains(event.target as Node)) {
        setShowTemplateMenu(false);
      }
      if (snippetMenuRef.current && !snippetMenuRef.current.contains(event.target as Node)) {
        setShowSnippetMenu(false);
      }
      if (mergeTagMenuRef.current && !mergeTagMenuRef.current.contains(event.target as Node)) {
        setShowMergeTagMenu(false);
      }
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target as Node)) {
        setShowHelp(false);
      }
      if (aiPanelRef.current && !aiPanelRef.current.contains(event.target as Node) && !aiLoadingRef.current) {
        setShowAiPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Inserisci snippet
  const insertSnippet = useCallback((snippetHtml: string) => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const fragment = range.createContextualFragment(snippetHtml);
        range.insertNode(fragment);
        handleInput();
      } else {
        // Se non c'è selezione, inserisci alla fine
        editorRef.current.innerHTML += snippetHtml;
        handleInput();
      }
      editorRef.current.focus();
    }
    setShowSnippetMenu(false);
  }, [handleInput]);

  // Inserisci HTML arbitrario (es. media) alla posizione del cursore
  const insertHtmlAtCursor = useCallback((html: string) => {
    if (isHtmlMode) {
      onChange((value ? value + '\n' : '') + html);
      textareaRef.current?.focus();
      return;
    }
    if (editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && editorRef.current.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const fragment = range.createContextualFragment(html);
        range.insertNode(fragment);
      } else {
        editorRef.current.innerHTML += html;
      }
      handleInput();
    }
  }, [isHtmlMode, value, onChange, handleInput]);

  // Incolla ripulito da carattere, dimensione e interlinea (solo con pulisciStiliCarattere)
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    if (!pulisciCarattere) return;
    const html = e.clipboardData.getData('text/html');
    const testo = e.clipboardData.getData('text/plain');
    if (!html && !testo) return;
    e.preventDefault();
    const pulito = html ? pulisciStiliCarattere(html) : testoInHtml(testo);
    if (typeof document.execCommand === 'function' && document.execCommand('insertHTML', false, pulito)) {
      handleEditorInput();
    } else {
      insertHtmlAtCursor(pulito);
    }
  }, [pulisciCarattere, handleEditorInput, insertHtmlAtCursor]);

  // Inserisci merge tag
  const insertMergeTag = useCallback((tag: string) => {
    if (isHtmlMode && textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const newText = text.substring(0, start) + tag + text.substring(end);
      onChange(newText);
      // Restore cursor position after insertion
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + tag.length;
        textarea.focus();
      }, 0);
    } else if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, tag);
      handleInput();
    }
    setShowMergeTagMenu(false);
  }, [isHtmlMode, onChange, handleInput]);

  return (
    <div className="rich-editor-wrapper relative" ref={wrapperRef}>
      {/* Toolbar */}
      <div className={`editor-toolbar bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-t-lg p-3 flex gap-2 ${compact ? 'editor-toolbar-compact flex-nowrap overflow-x-auto' : 'flex-wrap'}`}>
        {/* Mode toggle */}
        <div className="flex gap-1 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            onClick={toggleHtmlMode}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              isHtmlMode
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
            }`}
            title={isHtmlMode ? 'Modalità Visuale' : 'Modalità HTML'}
          >
            {isHtmlMode ? '👁️ Visuale' : '🔧 HTML'}
          </button>
        </div>

        {/* Formatting buttons - solo in modalità visuale */}
        {!isHtmlMode && (
          <div className="flex gap-1 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            onClick={() => execCommand('bold')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Grassetto"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => execCommand('italic')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Corsivo"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => execCommand('underline')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Sottolineato"
          >
            <u>U</u>
          </button>
          </div>
        )}

        {/* Headings - solo in modalità visuale */}
        {!isHtmlMode && (
          <div className="flex gap-1 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            onClick={() => execCommand('formatBlock', 'h2')}
            className="px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
            title="Titolo H2"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => execCommand('formatBlock', 'h3')}
            className="px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
            title="Titolo H3"
          >
            H3
          </button>
          <button
            type="button"
            onClick={() => execCommand('formatBlock', 'p')}
            className="px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
            title="Paragrafo"
          >
            P
          </button>
          </div>
        )}

        {/* Lists - solo in modalità visuale */}
        {!isHtmlMode && (
          <div className="flex gap-1 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            onClick={() => execCommand('insertUnorderedList')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Lista puntata"
          >
            •
          </button>
          <button
            type="button"
            onClick={() => execCommand('insertOrderedList')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Lista numerata"
          >
            1.
          </button>
          </div>
        )}

        {/* Link e altri - solo in modalità visuale */}
        {!isHtmlMode && (
          <div className="flex gap-1 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            onClick={insertLink}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Inserisci link"
          >
            🔗
          </button>
          <button
            type="button"
            onClick={insertHorizontalRule}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Linea orizzontale"
          >
            ―
          </button>
          <button
            type="button"
            onClick={removeFormatting}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Rimuovi formattazione"
          >
            🧹
          </button>
          </div>
        )}

        {/* Alignment - solo in modalità visuale */}
        {!isHtmlMode && !compact && (
          <div className="flex gap-1 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            onClick={() => execCommand('justifyLeft')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Allinea a sinistra"
          >
            ⬅
          </button>
          <button
            type="button"
            onClick={() => execCommand('justifyCenter')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Centra"
          >
            ↔
          </button>
          <button
            type="button"
            onClick={() => execCommand('justifyRight')}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Allinea a destra"
          >
            ➡
          </button>
          </div>
        )}

        {/* Image upload button */}
        <div className="flex gap-1 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            onClick={openImagePicker}
            disabled={disabled || isUploadingImage}
            className="px-3 py-1 rounded bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title="Carica immagine"
          >
            {isUploadingImage ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm">Caricamento...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm">Immagine</span>
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          {!hideMedia && (
          <button
            type="button"
            onClick={() => setShowMediaModal(true)}
            disabled={disabled}
            className="px-3 py-1 rounded bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title="Inserisci audio, video o YouTube"
          >
            <span aria-hidden="true">🎬</span>
            <span className="text-sm">Media</span>
          </button>
          )}
        </div>

        {/* AI refining - solo editor articoli + permesso */}
        {showAiButton && (
          <div className="relative" ref={aiPanelRef}>
            <button
              type="button"
              onClick={() => (showAiPanel ? setShowAiPanel(false) : openAiPanel())}
              disabled={disabled}
              className="px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Modifica con l'AI"
            >
              🤖 AI
            </button>

            {showAiPanel && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl z-50 p-3">
                <div className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                  Ambito:{' '}
                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                    {aiScope === 'selection' ? 'Testo selezionato' : (aiRefine ? 'Tutto il testo' : 'Tutto l\'articolo')}
                  </span>
                </div>
                {aiAvviso && (
                  <p role="note" className="mb-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-900/25 dark:text-amber-200">
                    {aiAvviso}
                  </p>
                )}
                <textarea
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  disabled={aiLoading}
                  rows={3}
                  maxLength={500}
                  placeholder="Es: rendi il testo più formale"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
                {aiError && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{aiError}</p>
                )}
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowAiPanel(false); setAiInstruction(''); setAiError(''); }}
                    disabled={aiLoading}
                    className="px-3 py-1 rounded text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={handleAiApply}
                    disabled={aiLoading}
                    className="px-3 py-1 rounded text-sm bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {aiLoading && (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                    )}
                    {aiLoading ? 'Elaboro...' : 'Applica'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Annulla ultima modifica AI */}
        {showAiButton && hasAiSnapshot && (
          <button
            type="button"
            onClick={handleAiUndo}
            disabled={disabled}
            className="disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1 rounded border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 text-sm hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors flex items-center gap-1"
            title="Ripristina la bozza precedente alla modifica AI"
          >
            ↩ Annulla modifica AI
          </button>
        )}

        {/* Templates dropdown - UNASCA */}
        {!compact && (
        <div className="relative" ref={templateMenuRef}>
          <button
            type="button"
            onClick={() => setShowTemplateMenu(!showTemplateMenu)}
            className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            📄 Template
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showTemplateMenu && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
              {articleTemplates.map((template, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    insertTemplate(template.content);
                    setShowTemplateMenu(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">{template.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{template.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Snippets dropdown - UNASCA */}
        {!compact && (
        <div className="relative" ref={snippetMenuRef}>
          <button
            type="button"
            onClick={() => setShowSnippetMenu(!showSnippetMenu)}
            className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            ✨ Elementi
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showSnippetMenu && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
              <button
                type="button"
                onClick={() => insertSnippet(articleSnippets.kicker)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
              >
                🏷️ Kicker (Categoria)
              </button>
              <button
                type="button"
                onClick={() => insertSnippet(articleSnippets.lead)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
              >
                📝 Lead (Introduzione)
              </button>
              <button
                type="button"
                onClick={() => insertSnippet(articleSnippets.keypoints)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
              >
                📌 Box Punti Chiave
              </button>
              <button
                type="button"
                onClick={() => insertSnippet(articleSnippets.callout)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
              >
                💡 Callout (Nota)
              </button>
              <button
                type="button"
                onClick={() => insertSnippet(articleSnippets.badge)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
              >
                🏷️ Badge
              </button>
              <button
                type="button"
                onClick={() => insertSnippet(articleSnippets.separator)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
              >
                ➖ Separatore
              </button>
              <button
                type="button"
                onClick={() => insertSnippet(articleSnippets.footer)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                📄 Footer
              </button>
            </div>
          )}
        </div>
        )}

        {/* Merge Tags dropdown - Campi dinamici */}
        {mergeTags && mergeTags.length > 0 && (
          <div className="relative" ref={mergeTagMenuRef}>
            <button
              type="button"
              onClick={() => setShowMergeTagMenu(!showMergeTagMenu)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              🏷️ Campi
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showMergeTagMenu && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                {mergeTags.map((group) => (
                  <div key={group.category}>
                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 sticky top-0">
                      {group.category}
                    </div>
                    {group.tags.map((t) => (
                      <button
                        key={t.tag}
                        type="button"
                        onClick={() => insertMergeTag(t.tag)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-700 dark:text-gray-300 flex items-center justify-between"
                      >
                        <span>{t.label}</span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">{t.tag}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HTML Mode helpers */}
        {isHtmlMode && (
          <div className="flex gap-1 border-l border-gray-300 dark:border-gray-600 pl-2">
            <button
              type="button"
              onClick={() => {
                try {
                  const formatted = value
                    .replace(/></g, '>\n<')
                    .replace(/^\s+|\s+$/gm, '')
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line.length > 0)
                    .join('\n');
                  onChange(formatted);
                } catch (e) {
                  console.warn('Errore formattazione HTML:', e);
                }
              }}
              className="px-2 py-1 rounded text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
              title="Formatta HTML"
            >
              📐 Formatta
            </button>
            <button
              type="button"
              onClick={() => {
                const wordCount = value.replace(/<[^>]*>/g, '').trim().split(/\s+/).length;
                alert(`Parole: ${wordCount}\nCaratteri: ${value.length}\nCaratteri senza HTML: ${value.replace(/<[^>]*>/g, '').length}`);
              }}
              className="px-2 py-1 rounded text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
              title="Statistiche"
            >
              📊 Stats
            </button>
          </div>
        )}

        {/* Help button */}
        {!compact && (
        <div className="relative ml-auto" ref={helpMenuRef}>
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="px-3 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors flex items-center gap-1"
            title="Guida Template UNASCA"
          >
            ❓ Guida
          </button>

          {showHelp && (
            <div className="absolute top-full right-0 mt-1 w-96 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl z-50 p-4 max-h-96 overflow-y-auto">
              <h3 className="font-bold text-base mb-3 text-gray-900 dark:text-gray-100">📚 Guida Template UNASCA</h3>

              <div className="space-y-3 text-sm">
                <div>
                  <h4 className="font-semibold text-sm text-gray-800 dark:text-gray-200 mb-1">📄 Template</h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Usa il menu <strong>"Template"</strong> per inserire strutture complete di articoli pre-formattate.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-sm text-gray-800 dark:text-gray-200 mb-1">✨ Elementi</h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Usa il menu <strong>"Elementi"</strong> per inserire singoli componenti stilizzati:
                  </p>
                  <ul className="list-disc list-inside mt-1 text-gray-600 dark:text-gray-400 space-y-1">
                    <li><strong>Kicker:</strong> Categoria in alto (es. "COMUNICAZIONE")</li>
                    <li><strong>Lead:</strong> Introduzione evidenziata</li>
                    <li><strong>Punti Chiave:</strong> Box con elenco puntato</li>
                    <li><strong>Callout:</strong> Nota importante evidenziata</li>
                    <li><strong>Badge:</strong> Etichetta colorata (es. "NUOVO")</li>
                    <li><strong>Separatore:</strong> Linea divisoria</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-sm text-gray-800 dark:text-gray-200 mb-1">🎨 Stili Automatici</h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Gli stili UNASCA sono già caricati globalmente. Tutti gli elementi inseriti verranno visualizzati correttamente sia nell'editor che nella pagina pubblica.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-sm text-gray-800 dark:text-gray-200 mb-1">🔧 Modalità HTML</h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Passa alla modalità HTML per modificare direttamente il codice. Usa il bottone <strong>"Formatta"</strong> per rendere il codice più leggibile.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowHelp(false)}
                className="mt-4 w-full px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Mode indicator (nascosto in modalita compatta, es. verbale associati) */}
      {!compact && (
        <div className="mode-indicator">
          {isHtmlMode ? '🔧 HTML' : '👁️ Visuale'}
        </div>
      )}

      {/* Editor */}
      {isHtmlMode ? (
        /* Modalità HTML */
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleHtmlChange}
            disabled={disabled}
            placeholder="Inserisci il codice HTML qui..."
            className="html-editor w-full border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white font-mono text-sm resize-none"
            style={{ minHeight: height }}
          />
        </div>
      ) : (
        /* Modalità Visuale */
        <div className="relative">
          <div
            ref={editorRef}
            contentEditable={!disabled}
            onInput={handleEditorInput}
            onPaste={handlePaste}
            className={`editor-content border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white prose prose-lg max-w-none${editorClassName ? ` ${editorClassName}` : ''}`}
            style={{ minHeight: height }}
            data-placeholder={placeholder}
            suppressContentEditableWarning={true}
          />
        </div>
      )}

      {/* Bolla delle azioni AI sopra il testo selezionato */}
      {bollaAbilitata && bolla && azioniRapide && (
        <div
          role="toolbar"
          aria-label="Azioni AI sulla selezione"
          className="absolute z-50 flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg p-1 whitespace-nowrap"
          style={{
            top: bolla.top,
            left: bolla.left,
            transform: bolla.sotto ? 'translateX(-50%)' : 'translate(-50%, -100%)'
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {azioniRapide.map((azione, indice) => (
            <button
              key={`${indice}-${azione.etichetta}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAzioneRapida(indice)}
              disabled={aiLoading}
              title={azione.istruzione}
              className="px-2 py-1 rounded text-sm text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              {bollaInCorso === indice && (
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              )}
              {azione.etichetta}
            </button>
          ))}
        </div>
      )}

      {/* Modale inserimento media (audio/video/YouTube) */}
      <MediaEmbedModal
        isOpen={showMediaModal}
        onClose={() => setShowMediaModal(false)}
        onInsert={insertHtmlAtCursor}
      />
    </div>
  );
};

export default RichEditor;
